import { useEffect, useRef } from 'preact/hooks';
import { qs } from '../api';
import { ROTULO_STATUS, dataCurta, decimal, inteiro, pct, plural } from '../format';
import { useApi } from '../hooks';
import { pesquisaId, secretariaId } from '../state';
import type { DetalheMunicipio, PontoSerie, ResultadoMunicipioPesquisa } from '../types';
import { GraficoLinha } from './GraficoLinha';
import { IndicadoresMunicipio } from './IndicadoresMunicipio';

type Props = { cod: string; data: string | undefined; onFechar: () => void };

export function DrawerMunicipio({ cod, data, onFechar }: Props) {
  const fecharRef = useRef<HTMLButtonElement>(null);
  const detalhe = useApi<DetalheMunicipio>(`/painel/municipios/${cod}${qs({ data, secretariaId: secretariaId.value })}`);
  const serie = useApi<{ pontos: PontoSerie[] }>(`/painel/serie${qs({ municipio: cod, secretariaId: secretariaId.value })}`);
  // Satisfação só aparece se há pesquisa escolhida e o perfil pode ler resultados (senão a API responde 403/404 e some).
  const satisfacao = useApi<ResultadoMunicipioPesquisa>(pesquisaId.value ? `/pesquisas/${pesquisaId.value}/resultado${qs({ municipio: cod, data })}` : null);

  useEffect(() => {
    fecharRef.current?.focus();
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && onFechar();
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const d = detalhe.dados;
  const sat = satisfacao.dados;

  return (
    <aside class="drawer" role="dialog" aria-label={d ? `Detalhes de ${d.municipio.nome}` : 'Detalhes do município'}>
      <header class="drawer-topo">
        <div>
          <h2>{d?.municipio.nome ?? '…'}</h2>
          {d && <p class="dica">{d.municipio.mesorregiao} · {d.municipio.microrregiao}</p>}
        </div>
        <button type="button" ref={fecharRef} class="fechar" onClick={onFechar} aria-label="Fechar detalhes">✕</button>
      </header>

      {detalhe.erro && <p class="erro" role="alert">{detalhe.erro.message}</p>}

      {d && (
        <div class="drawer-corpo">
          <p class="dica">{d.aoVivo ? 'Situação atual' : `Situação em ${dataCurta(d.data, true)}`}</p>
          <div class="drawer-kpis">
            <div class="kpi"><span class="kpi-valor">{pct(d.resumo.progressoMedio)}</span><span class="kpi-rotulo">progresso médio</span></div>
            <div class="kpi"><span class="kpi-valor">{inteiro(d.resumo.total)}</span><span class="kpi-rotulo">ações</span></div>
            <div class="kpi"><span class="kpi-valor">{inteiro(d.resumo.concluidas)}</span><span class="kpi-rotulo">concluídas</span></div>
            <div class="kpi kpi-alerta"><span class="kpi-valor">{inteiro(d.resumo.atrasadas)}</span><span class="kpi-rotulo">atrasadas</span></div>
          </div>

          {sat && (
            <section class="bloco">
              <h3>Satisfação</h3>
              {sat.disponivel && sat.resumo ? (
                <p>
                  <strong class="grande">{pct(sat.resumo.aprovacao)}</strong> aprovam o governo
                  {sat.resumo.margemErro !== null && <> (±{decimal(sat.resumo.margemErro)} p.p.)</>}
                  <small class="bloco-nota">{plural(sat.n, 'resposta', 'respostas')}{sat.meta ? ` de ${inteiro(sat.meta)} previstas` : ''}</small>
                </p>
              ) : (
                <p class="vazio">Amostra insuficiente para divulgar: {plural(sat.n, 'resposta', 'respostas')} (mínimo {inteiro(sat.amostraMinima)}).</p>
              )}
            </section>
          )}

          <IndicadoresMunicipio cod={cod} />

          <section class="bloco">
            <h3>Evolução neste município</h3>
            <GraficoLinha pontos={(serie.dados?.pontos ?? []).map((p) => ({ data: p.data, valor: p.progresso }))} rotulo={`Progresso em ${d.municipio.nome}`} />
          </section>

          <section class="bloco">
            <h3>Ações por secretaria</h3>
            {d.porSecretaria.length === 0 && <p class="vazio">Nenhuma ação cadastrada neste município{secretariaId.value ? ' para a secretaria filtrada' : ''}.</p>}
            {d.porSecretaria.map((s) => (
              <details key={s.secretariaId} class="secao" open={d.porSecretaria.length === 1}>
                <summary>
                  <span><i class="ponto" style={{ background: s.cor }} />{s.sigla}</span>
                  <span class="resumo-sec">{plural(s.total, 'ação', 'ações')} · {pct(s.progressoMedio)}</span>
                </summary>
                <ul class="acoes">
                  {s.acoes.map((a) => (
                    <li key={a.id} class={a.status === 'CANCELADA' ? 'cancelada' : ''}>
                      <div class="acao-topo">
                        <span class="acao-titulo">{a.titulo}</span>
                        <strong>{a.progressoPct}%</strong>
                      </div>
                      <div class="trilho"><div class="preench" style={{ width: `${a.progressoPct}%`, background: s.cor }} /></div>
                      <div class="acao-meta">
                        <span class={`selo selo-${a.status.toLowerCase()}`}>{ROTULO_STATUS[a.status]}</span>
                        {a.atrasada && <span class="selo selo-atrasada">Atrasada</span>}
                        <small>{a.projeto}{a.dataFimPrevista ? ` · prazo ${dataCurta(a.dataFimPrevista, true)}` : ''}</small>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </section>
        </div>
      )}
    </aside>
  );
}
